import { AxiosError } from "axios";
import { toast } from "sonner";
function extractErrorsFromCollection(errors: unknown): string[] {
  const errorMessages: string[] = [];

  if (Array.isArray(errors)) {
    errors.forEach((err) => {
      if (typeof err === "string") {
        errorMessages.push(err);
      }
      else if (typeof err === "object" && err !== null) {
        const errObj = err as Record<string, unknown>;
        if (typeof errObj.detail === "string") {
          errorMessages.push(errObj.detail);
        }
        else if (typeof errObj.message === "string") {
          errorMessages.push(errObj.message);
        }
      }
    });
    return errorMessages;
  }

  if (typeof errors === "object" && errors !== null) {
    Object.values(errors).forEach((fieldErrors: unknown) => {
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach((err) => {
          if (typeof err === "string") {
            errorMessages.push(err);
          }
          else if (typeof err === "object" && err !== null) {
            const errObj = err as Record<string, unknown>;
            if (typeof errObj.detail === "string") {
              errorMessages.push(errObj.detail);
            }
            else if (typeof errObj.message === "string") {
              errorMessages.push(errObj.message);
            }
          }
        });
      }
      else if (typeof fieldErrors === "string") {
        errorMessages.push(fieldErrors);
      }
    });
  }

  return errorMessages;
}

function extractMessageFromResponseData(responseData: unknown): string | null {
  if (typeof responseData !== "object" || responseData === null) {
    return typeof responseData === "string" ? responseData : null;
  }

  const data = responseData as Record<string, unknown>;

  if (typeof data.message === "string") return data.message;
  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;

  if (typeof data.title === "string") {
    const title = data.title;
    const detail = typeof data.detail === "string" ? data.detail : null;
    return detail ? `${title}: ${detail}` : title;
  }

  if (data.errors) {
    const errorMessages = extractErrorsFromCollection(data.errors);
    if (errorMessages.length > 0) {
      return errorMessages.join(", ");
    }
  }

  return null;
}

function extractMessageFromAxiosError(axiosError: AxiosError): string {
  // Essayer d'extraire le message depuis la réponse de l'API
  if (axiosError.response?.data) {
    const message = extractMessageFromResponseData(axiosError.response.data);
    if (message) return message;
  }

  // Fallback sur le message HTTP standard
  if (axiosError.response?.statusText) {
    return `${axiosError.response.status}: ${axiosError.response.statusText}`;
  }

  // Fallback sur le message de l'erreur Axios
  if (axiosError.message) {
    return axiosError.message;
  }

  return "Erreur de communication avec le serveur";
}

function isAxiosError(error: unknown): error is AxiosError {
  return error !== null &&
    typeof error === "object" &&
    "isAxiosError" in error;
}

export function extractApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    return extractMessageFromAxiosError(error);
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Une erreur inattendue s'est produite";
}

/**
 * Extracts only the server-provided error message (validation / business rule)
 * from an API error response. Unlike extractApiErrorMessage, it does NOT fall
 * back to the HTTP status or the network/Axios message, returning null instead.
 *
 * This lets callers keep their own generic message and surface the backend
 * detail (e.g. "Téléphone portable est déjà utilisé.") only when one exists,
 * without polluting the UI with noise like "500: Internal Server Error".
 */
export function extractApiErrorDetail(error: unknown): string | null {
  if (isAxiosError(error) && error.response?.data) {
    return extractMessageFromResponseData(error.response.data);
  }

  return null;
}

export function isAuthFailure(error: unknown): boolean {
  if (
    typeof error === "object" &&
    error !== null &&
    "__isAuthFailure" in error &&
    (error).__isAuthFailure === true
  ) {
    return true;
  }

  // Fallback: 401 on a retried request = auth is dead
  if (
    isAxiosError(error) &&
    error.response?.status === 401 &&
    (error.config as { _retry?: boolean })?._retry === true
  ) {
    return true;
  }

  return false;
}

export function isForbiddenError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403;
}

export function isNotFoundError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

export function is422Error(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 422;
}

/** Toute erreur 4xx : une réponse délibérée du serveur (validation, refus
 * métier, introuvable). La rejouer à l'identique redonnera la même réponse -
 * un retry n'a de sens que sur les erreurs de transport et les 5xx. */
export function isClientError(error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
}

/** 409 : la demande était valide, c'est l'état du serveur qui a bougé sous
 * elle. Sur une libération Bridge, cela veut dire que le consentement a été
 * donné entre-temps, donc que le virement est parti. */
export function isConflictError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 409;
}

export function extract422ErrorsDescription(error: unknown): string | undefined {
  if (!isAxiosError(error) || error.response?.status !== 422) return undefined;
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data?.errors || !Array.isArray(data.errors)) return undefined;
  const messages = data.errors.filter((e): e is string => typeof e === "string");
  return messages.length > 0 ? messages.join("\n") : undefined;
}

export const extractAxiosErrorKey = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { data?: { error?: string } } };
    return axiosError.response?.data?.error || "";
  }
  return "";
};

export function toastErrorIfNotForbidden(error: unknown, message: string, options?: Parameters<typeof toast.error>[1]) {
  if (!isForbiddenError(error)) {
    if (options !== undefined) {
      toast.error(message, options);
    }
    else {
      toast.error(message);
    }
  }
}
